import React, {FC, useContext, useEffect, useMemo, useRef} from 'react';
import styled from 'styled-components';

import {
  useBuildCancelToken,
  useInterval,
} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {LayerVisibilityContext} from '../../../../libs/shared/ui/src/components/layers/layerVisibilityContext';
import {useDocumentVisible} from '../../../../libs/shared/ui/src/hooks/focusHookHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {setExpandItemAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  analyticsLiveDashboardFeedbackBannerCollapseKey,
  getAnalyticsLiveDashboardFeedbackBannerStatus,
  getOptionalPoppedOutAnalyticsPanelId,
} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {AnalyticsParameters} from '../../../store/panels/analytics/parameters/analyticsParameters';
import {fetchLiveDashboardContentAction} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {makeGetAnalyticsLiveDashboardFetchTimestamp} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {makeAnalyticsReportTypeUrl} from '../../helpers/analyticsUrlHelpers';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {BetaBanner} from '../reportViewer/betaBanner';
import {LIVE_DASHBOARD_AUTO_REFRESH_PERIOD_MS} from './constants';
import {LiveDashboard} from './liveDashboard';
import {LiveDashboardOtherPoppedOutMessage} from './liveDashboardOtherPoppedOutMessage';

/*
 * Styles.
 */

const StyledLiveDashboardWrapperDiv = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;

  overflow-y: hidden;
  overflow-x: auto;
`;

/*
 * Props.
 */

export interface LiveDashboardContainerProps {
  panelId: AnalyticsPanelId;
  parameters: AnalyticsParameters;
  isBetaBannerOpen: boolean;
  setIsBetaBannerOpen?: (value: boolean) => void;
}

export const LiveDashboardContainer: FC<LiveDashboardContainerProps> = ({
  panelId,
  parameters,
  isBetaBannerOpen,
  setIsBetaBannerOpen,
}) => {
  const dispatch = useAppDispatch();
  const [setIntervalFunc, clearIntervalFunc] = useInterval();

  // TODO(ldemarne): Use panel's cancel token.
  const {buildCancelToken} = useBuildCancelToken();

  const getLiveDashboardFetchedAt = useMemo(
    () => makeGetAnalyticsLiveDashboardFetchTimestamp(() => panelId),
    [panelId],
  );
  const liveDashboardFetchedAt = useAppSelector(getLiveDashboardFetchedAt);
  const firstBetaBannerIsUntouched = useAppSelector(getAnalyticsLiveDashboardFeedbackBannerStatus);

  const poppedOutPanelId = useAppSelector(getOptionalPoppedOutAnalyticsPanelId);
  const isLiveDashboardPoppedOut = isDefined(poppedOutPanelId);
  const isThisPanelPoppedOut = poppedOutPanelId === panelId;
  const isOtherPanelPoppedOut = isLiveDashboardPoppedOut && !isThisPanelPoppedOut;

  const {window} = useLayer();
  const isDocumentVisible = useDocumentVisible(window);

  // When the Live dashboard is popped out, we don't want to take panel visibility into account.
  // This lets users navigate the inbox and look at the dashboard at the same time.
  const layerVisibilityContextValue = useContext(LayerVisibilityContext);
  const isPanelVisible = layerVisibilityContextValue.isVisible || isThisPanelPoppedOut;

  const isVisible = isDocumentVisible && isPanelVisible;

  const shouldAutoRefresh = !isOtherPanelPoppedOut && isVisible;
  const isRefreshEnabledRef = useRef(shouldAutoRefresh);
  const isStale =
    isDefined(liveDashboardFetchedAt) &&
    Date.now() - liveDashboardFetchedAt > LIVE_DASHBOARD_AUTO_REFRESH_PERIOD_MS;

  const closeBanner = () => {
    if (setIsBetaBannerOpen) {
      setIsBetaBannerOpen(false);
      dispatch(setExpandItemAction(analyticsLiveDashboardFeedbackBannerCollapseKey, false));
    }
  };

  useInitialEffect(() => {
    setIntervalFunc(() => {
      if (isRefreshEnabledRef.current) {
        dispatch(fetchLiveDashboardContentAction(panelId, buildCancelToken()));
      }
    }, LIVE_DASHBOARD_AUTO_REFRESH_PERIOD_MS);

    return clearIntervalFunc;
  });

  useEffect(() => {
    isRefreshEnabledRef.current = shouldAutoRefresh;

    if (shouldAutoRefresh && isStale) {
      // Refresh on transition.
      dispatch(fetchLiveDashboardContentAction(panelId, buildCancelToken()));
    }
  }, [buildCancelToken, dispatch, isRefreshEnabledRef, isStale, panelId, shouldAutoRefresh]);

  useEffect(() => {
    const {namespace} = parameters;

    if (isTeammateNamespace(namespace) || computeIsGlobalNamespace(namespace)) {
      // Redirect to Workload report if changing to a teammate or global namespace while on the page.
      dispatch(navigateToLinkAction(makeAnalyticsReportTypeUrl(AnalyticsReportsEnum.WORKLOAD, parameters)));
    }
  }, [dispatch, parameters]);

  if (isOtherPanelPoppedOut) {
    return <LiveDashboardOtherPoppedOutMessage poppedOutPanelId={poppedOutPanelId} />;
  }

  return (
    <StyledLiveDashboardWrapperDiv>
      {isBetaBannerOpen ? (
        <BetaBanner
          panelId={panelId}
          onClose={closeBanner}
          isDisplayedForTheFirstTime={firstBetaBannerIsUntouched}
        />
      ) : (
        <div /> // We need this dummy div or the layout gets messed up, see `StyledLiveDashboardWrapperDiv`.
      )}
      <LiveDashboard
        panelId={panelId}
        parameters={parameters}
        isThisPoppedOut={isThisPanelPoppedOut}
        isLiveDashboardPoppedOut={isLiveDashboardPoppedOut}
        isBetaBannerOpen={isBetaBannerOpen}
      />
    </StyledLiveDashboardWrapperDiv>
  );
};
